import { DateTime } from "luxon";
import { toast } from "react-hot-toast";

import { Accession, LISCaseList, Specimen } from "pwapi";

type ModMedPayload = {
  ln: string;
  fn: string;
  dob: string;
  pid: string;
  mrn: string;
  o: string;
  t: string;
  location: string;
  dos: string;
};
export type ModMedPWPayload = {
  last_name: string;
  first_name: string;
  date_of_birth: string;
  external_patient_id: string;
  medical_record_number: string;
  external_order_id: string;
  specimen: string;
  specimen_name?: string;
  location: string;
  collected_date: string;
};
export type BarcodePayload = {
  barcode?: string;
  object_type: string;
  object_id: number; // NOTE FOR CODE REVIEW: this wouldn't exist in a "mod_med_payload". How to handle this as elegantly as possible?
  object_token?: string;
  mod_med_payload?: ModMedPWPayload;
  specimen_bag_payload?: string;
  station?: string;
};

export const accessionFromPayload = (
  mod_med_payload: ModMedPWPayload
): Partial<Accession> => {
  return {
    patient: {
      first_name: mod_med_payload.first_name,
      last_name: mod_med_payload.last_name,
      date_of_birth: DateTime.fromFormat(
        mod_med_payload.date_of_birth,
        "MM/dd/yyyy"
      ).toISODate(),
      external_patient_id: mod_med_payload.external_patient_id,
      medical_record_number: mod_med_payload.medical_record_number,
    },
    specimens: [
      {
        name: mod_med_payload.specimen_name,
        site: mod_med_payload.location,
        lis_state: Specimen.lis_state.BOTTLED,
      },
    ],
    // dos="03/28/2023"
    /*
      Removed "collected_date" via dos from payload
      May not be accurate per barcode to req form
      collected_date: DateTime.fromFormat(
        mod_med_payload.collected_date,
        "MM/dd/yyyy"
      ).toISODate(),
    */
    // @ts-ignore: external_order_id should be a string
    external_order_id: mod_med_payload.external_order_id,
  };
};

export const modMedXMLtoJSON = (xml: XMLDocument): ModMedPayload => {
  const payload = [...xml.getElementsByTagName("mm")].at(0);

  if (payload) {
    return payload.getAttributeNames().reduce(
      (json, attribute) => ({
        ...json,
        [attribute]: payload.getAttribute(attribute),
      }),
      {} as ModMedPayload
    );
  } else {
    throw new Error("No Payload");
  }
};

export const modMedtoPW = (modMed: ModMedPayload) => {
  const specimen_name = modMed["t"]?.split("-").at(1);
  const location = modMed["location"]?.replace(`${specimen_name}. `, "");

  return {
    first_name: modMed["fn"],
    last_name: modMed["ln"],
    date_of_birth: modMed["dob"],
    external_patient_id: modMed["pid"],
    medical_record_number: modMed["mrn"],

    external_order_id: modMed["o"],

    specimen: modMed["t"],
    specimen_name,
    location,
    collected_date: modMed["dos"],
  };
};

export const mmBarcodeFromSpecimen = (
  caseState: LISCaseList,
  specimen: LISCaseList["specimens"][0]
) => {
  return `<mm ln="${caseState.patient?.last_name}" fn="${caseState.patient?.first_name}" dob="${caseState.patient?.date_of_birth}" pid="${caseState.patient?.external_patient_id}" mrn="${caseState.patient?.medical_record_number}" o="${caseState.external_order_id}" t="${caseState.external_order_id}-${specimen.name}" location="${specimen.name}. ${specimen.site}" dos="${specimen.collected_date}" />`;
};

export const mmXmlFromString = (code: string) => {
  const parser = new DOMParser();
  return parser.parseFromString(code, "text/xml");
};

export const modMedFromState = (
  caseState: LISCaseList,
  specimen: LISCaseList["specimens"][0]
) => {
  return modMedtoPW(
    modMedXMLtoJSON(mmXmlFromString(mmBarcodeFromSpecimen(caseState, specimen)))
  );
};

const objectFromBarcode = (
  barcode: string,
  bag_tracking?: boolean
): BarcodePayload => {
  if (barcode.includes("<mm ")) {
    if (barcode === barcode.toUpperCase()) {
      toast.error("Caps Lock may be on");
    }
    // ModMed Barcode
    //<<mm ln="Test" fn="Derm" dob="02/02/1965" pid="269592" mrn="123454345" o="5296" t="5296-A" location="A. left inferior central malar cheek" dos="03/28/2023" />

    const code = barcode.replace("<<", "<");
    const parser = new DOMParser();
    const payloadXML = parser.parseFromString(code, "text/xml");
    const mod_med_payload = modMedtoPW(modMedXMLtoJSON(payloadXML));

    return {
      object_type: "specimen",
      object_id: 0,
      mod_med_payload,
    };
  } else if (
    bag_tracking &&
    !barcode.includes("-") &&
    /^[A-Z]\d+$/.test(barcode.replace("<", ""))
  ) {
    return {
      object_type: "propath-specimen-bag",
      object_id: 0,
      specimen_bag_payload: barcode.replace("<", ""),
    };
  } else {
    if (barcode === barcode.toUpperCase()) {
      toast.error("Caps Lock may be on");
    }

    const split = barcode.replace("<", "").split("-");
    if (split[0] === "login") {
      const token = [...split];
      token.shift();
      return {
        object_type: split[0],
        object_id: 0,
        object_token: token.join("-"),
      };
    }

    return { object_type: split[0], object_id: parseInt(split[1]) };
  }
};

export default objectFromBarcode;

export type BarcodeObject = ReturnType<typeof objectFromBarcode>;
